/**
 * Which ground drops show a label, a beam and a click target.
 *
 * Promoted from `ideas/SYSTEM-LOOT-VISIBILITY-AND-ALT-TOGGLE.md`. **Most of that idea was already
 * built and stricter than it asks for**, which is worth stating so nobody re-implements it: the
 * loot filter decides visibility per drop, `DEFAULT_MIN_RARITY` is `RARE` so Normal and Magic
 * already get no label, `layoutLabels` already stacks colliding labels and reports a `+N more`
 * overflow, and beams already take their colour and width from the filter's tier.
 *
 * Three things were missing, and they are what this file adds:
 *
 * 1. **Hold Alt to reveal.** The filter's job is to hide things; without a way to see past it, a
 *    player who wants to check what a pack dropped has to open the editor and change a rule.
 * 2. **The drop impulse.** A brief flash when an item lands, so a kill gives feedback even for
 *    loot the filter would otherwise never mention.
 * 3. **Click-through for hidden drops.** A drop with no label is no longer a click target, so
 *    clicking the floor where one sits moves you instead of picking up something invisible.
 *
 * Pure, so it is testable without a scene.
 *
 * ## Ask through `lootLabelFor`, not `lootLabelVerdict`
 *
 * The rule needs five inputs and **every consumer must supply all five**. `LootPrompt` supplied
 * one — it asked the loot *filter* whether a drop was labelled, and suppressed its own copy of the
 * name on that basis. Holding **Alt** shows a label the filter hid, so the prompt kept printing the
 * name too and the nearest drop read as being listed twice. Reported as *"the item labels on the
 * ground are duplicated again when hitting alt"*; the same fault fires on a freshly-landed drop,
 * inside its impulse window, with no key held.
 *
 * `lootLabelFor` is the fix: it gathers the inputs from the store and applies the rule, so
 * "is this drop labelled" has one answer and adding a sixth condition cannot leave a consumer
 * behind. `lootLabelVerdict` stays exported for the tests that drive the rule directly.
 *
 * `game/loot.ts` still derives the **beam** from `beamStyle(verdict)` plus `revealed` separately.
 * That is not this rule and the header used to claim it was; a beam is a metre-tall pillar and a
 * label is text, and they are allowed to differ.
 */

/**
 * How long a freshly landed drop shows its name, in milliseconds.
 *
 * The idea says 1.5 s and that is right: long enough to read one word while the fight is ending,
 * short enough that a cleared pack's worth of drops has gone quiet before the player walks in.
 *
 * **It applies to drops the filter hides**, which is the whole point — a Normal item that lands
 * says so once and then stops. A drop the filter *shows* is unaffected; its label was already
 * permanent and an impulse would do nothing.
 */
export const DROP_IMPULSE_MS = 1_500;

/** How long the impulse spends fading, at the end of its window. */
export const IMPULSE_FADE_MS = 300;

/**
 * How far Alt reveals, in world units.
 *
 * The idea says 12 m and ties it to picking distance. Kept as its own constant rather than read
 * from `pickupRange()`: that is the server's rule about where a hand can reach, this is a choice
 * about how much floor to describe at once, and a server tuning its reach must not silently
 * change what a key shows.
 */
export const REVEAL_RANGE = 12;

/** Why a drop is being shown — for the class name, and to make a test's failure legible. */
export type LabelReason = 'FILTER' | 'REVEAL' | 'IMPULSE' | 'NONE';

export interface LootLabelVerdict {
  /** Draw the name. */
  showLabel: boolean;
  /** What decided it. */
  reason: LabelReason;
  /** 0–1, for the tail of the impulse. */
  opacity: number;
  /**
   * Whether the drop's collider should take a click.
   *
   * **Tied to the label, deliberately.** A drop the player cannot see must not eat a click aimed
   * at the floor or at a monster standing on it — the idea's "click-through mode", and the same
   * reasoning that keeps the loot-label layer `pointer-events: none`. It also means a hidden drop
   * is collected by holding Alt or by the `F` sweep rather than by a lucky click, which is the
   * intended trade and not an accident.
   */
  pickable: boolean;
}

export interface LootLabelContext {
  /** What the loot filter decided. A rule, or the built-in Rare-and-better fallback. */
  filterVisible: boolean;
  /** Alt is held. */
  revealed: boolean;
  /** When this drop first appeared in a snapshot, or undefined if that is unknown. */
  seenAt: number | undefined;
  /** The frame clock, in the same units as `seenAt`. */
  now: number;
  /** World-space distance from the player, or undefined when unknown. */
  distance: number | undefined;
}

const HIDDEN: LootLabelVerdict = {
  showLabel: false,
  reason: 'NONE',
  opacity: 0,
  pickable: false,
};

/**
 * The whole rule, in the idea's own order of precedence.
 *
 * 1. **The filter wins.** A drop a rule or the fallback shows is shown, at any distance and for
 *    ever. This is the player's own configuration and nothing here may quietly override it —
 *    which is also why Alt cannot *hide* anything.
 * 2. **Alt reveals**, within `REVEAL_RANGE`. Range-gated because "reveal everything on the map"
 *    is a second kind of clutter, and because a label needs an anchor on screen to be worth
 *    placing.
 * 3. **A fresh drop flashes**, wherever it landed and whatever the filter thinks, fading out over
 *    the last `IMPULSE_FADE_MS`.
 * 4. Otherwise nothing, and no click target.
 */
export function lootLabelVerdict(ctx: LootLabelContext): LootLabelVerdict {
  if (ctx.filterVisible) {
    return { showLabel: true, reason: 'FILTER', opacity: 1, pickable: true };
  }

  if (ctx.revealed && (ctx.distance === undefined || ctx.distance <= REVEAL_RANGE)) {
    return { showLabel: true, reason: 'REVEAL', opacity: 1, pickable: true };
  }

  if (ctx.seenAt !== undefined) {
    const age = ctx.now - ctx.seenAt;
    // A clock that ran backwards — a reconnect, or a fixture. Treat it as just-landed: a flash
    // that lingers is noticed and forgiven, one that never happens is the feature missing.
    if (age < 0) return { showLabel: true, reason: 'IMPULSE', opacity: 1, pickable: true };
    if (age < DROP_IMPULSE_MS) {
      const intoFade = age - (DROP_IMPULSE_MS - IMPULSE_FADE_MS);
      const opacity = intoFade <= 0 ? 1 : Math.max(0, 1 - intoFade / IMPULSE_FADE_MS);
      return { showLabel: true, reason: 'IMPULSE', opacity, pickable: true };
    }
  }

  return HIDDEN;
}

/**
 * The store surface the rule needs, as a structural type.
 *
 * Narrow on purpose: `GameStore` is not imported, so this file stays free of the store and
 * stays unit-testable against a literal. Anything shaped like this satisfies it.
 */
export interface LootLabelSource {
  filterVerdict(lootId: string): { visible: boolean } | undefined;
  lootRevealed(): boolean;
  frameNow(): number;
  groundSeenAtOf(lootId: string): number | undefined;
  self(): { x: number; z: number } | undefined;
}

/** A ground drop, in the terms this rule needs. */
export interface LootLabelDrop {
  id: string;
  x: number;
  z: number;
}

/**
 * Whether this drop is showing a label, asked the one way.
 *
 * Every consumer goes through here. `LootLabels` draws what it returns and `LootPrompt`
 * suppresses its own copy of the name when `showLabel` is true — which is only correct while both
 * are reading the *same* five inputs. They were not, and Alt made the difference visible.
 */
export function lootLabelFor(src: LootLabelSource, drop: LootLabelDrop): LootLabelVerdict {
  const me = src.self();
  return lootLabelVerdict({
    filterVisible: src.filterVerdict(drop.id)?.visible ?? false,
    revealed: src.lootRevealed(),
    seenAt: src.groundSeenAtOf(drop.id),
    now: src.frameNow(),
    distance: me ? Math.hypot(drop.x - me.x, drop.z - me.z) : undefined,
  });
}
